/**
 * Grouped channel multi-select for the Creators dashboard's create and edit
 * modals. Channels are grouped by ChannelMeter payee (creator/agency) —
 * picking a group selects every channel the creator runs, with checkboxes to
 * drop individual ones. Known clients with no payout yet (ChannelMeter,
 * Channel Mapping, Network CRM) are listed as "new" groups; the server
 * verifies them on save and their part of the dashboard opens in a welcome
 * view until the first payout lands.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getChannelGroupsByIds, searchChannelGroups } from '../../services/creatorDashboardsApi';
import { DashboardNotify } from '../../hooks/useDashboardToast';
import { fieldStyle } from './formParts';
import { ChannelGroup, GroupedChannelOption } from './types';

/**
 * A selected channel. `pending` marks an entry seeded from a saved page before
 * its display metadata has loaded — an explicit flag rather than inferring it
 * from the name, so a channel whose name happens to equal its id can't be
 * mistaken for one that is still unresolved.
 */
export type PickerChannel = GroupedChannelOption & { pending?: boolean };

export type PickerSelection = Map<string, PickerChannel>;

/** A known client with no ChannelMeter payout yet — its part of the dashboard opens in welcome mode. */
export const isPrePayout = (c: GroupedChannelOption): boolean => c.hasPayouts === false;

/**
 * Report pages assemble a ROLLING 12-month window, so a channel whose newest
 * earnings month is older than that renders an all-empty page. Flag those in
 * the picker before the admin builds a page around one. Cutoff = 12 full
 * months back from today — anchored a month early so a channel that is merely
 * lagging the current ingest is never flagged.
 */
const reportWindowFloorMonth = (() => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
})();

const isOutsideReportWindow = (lastMonth: string): boolean =>
  lastMonth !== '' && lastMonth < reportWindowFloorMonth;

/**
 * A complete @handle, youtube.com/@handle or youtube.com/channel/UC… URL —
 * Enter resolves it on YouTube (one channels.list call; typing never does).
 */
const looksLikeYouTubeChannel = (q: string): boolean => {
  const t = q.trim();
  return /^@[A-Za-z0-9._-]{1,60}$/.test(t) || /youtube\.com\/(@[A-Za-z0-9._-]{1,60}|channel\/UC[\w-]{22})/i.test(t);
};

const NewTag: React.FC = () => (
  <span
    style={{
      background: 'var(--accent-soft)',
      color: 'var(--accent-soft-fg)',
      borderRadius: 999,
      padding: '1px 7px',
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '0.02em',
      flexShrink: 0,
    }}
  >
    New
  </span>
);

const sectionLabelStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  background: 'var(--bg-surface-2)',
  borderBottom: '1px solid var(--border-1)',
};

/**
 * Grouped channel multi-select shared by the create and edit modals: debounced
 * search over ChannelMeter payee groups plus known clients with no payout yet,
 * tri-state group checkboxes, and removable pills for the current selection.
 * Enter looks the query up live (a channel URL or @handle on YouTube, a name
 * or email in ChannelMeter) for a client added after the server's cached list.
 * Pending entries are resolved in a single batch lookup on mount, then
 * anything a search reveals fills in the rest through the same path.
 */
export const ChannelPicker: React.FC<{
  selected: PickerSelection;
  onChange: React.Dispatch<React.SetStateAction<PickerSelection>>;
  maxChannels: number;
  capMessage: string;
  notify: DashboardNotify;
}> = ({ selected, onChange, maxChannels, capMessage, notify }) => {
  const [channelQuery, setChannelQuery] = useState('');
  const [groups, setGroups] = useState<ChannelGroup[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [searching, setSearching] = useState(true);
  const [lookedUpQuery, setLookedUpQuery] = useState<string | null>(null);
  const [resolved, setResolved] = useState<GroupedChannelOption[]>([]);
  const [manualExpand, setManualExpand] = useState<Map<string, boolean>>(new Map());
  const searchTimer = useRef<number | null>(null);
  const searchSeq = useRef(0);
  const initialSelection = useRef(selected);

  // Responses can land out of order; only the newest request may set state.
  const runSearch = useCallback((query: string, lookup: boolean) => {
    const seq = ++searchSeq.current;
    setSearching(true);
    searchChannelGroups(query, { lookup })
      .then((r) => {
        if (seq !== searchSeq.current) return;
        setGroups(r.groups);
        setWarnings(r.warnings);
      })
      .catch((err) => {
        if (seq !== searchSeq.current) return;
        setGroups([]);
        setWarnings([err?.message || 'Channel search failed — try again.']);
      })
      .finally(() => { if (seq === searchSeq.current) setSearching(false); });
  }, []);

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchSeq.current += 1; // drop any response for the previous text
    setSearching(true);
    searchTimer.current = window.setTimeout(() => runSearch(channelQuery, false), 250);
    return () => { if (searchTimer.current) window.clearTimeout(searchTimer.current); };
  }, [channelQuery, runSearch]);

  useEffect(() => { setManualExpand(new Map()); }, [channelQuery]);

  const lookUp = () => {
    const q = channelQuery.trim();
    if (q.length < 2) return;
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    setLookedUpQuery(q);
    runSearch(q, true);
  };

  // One batched lookup for the roster this picker opened with (edit modal).
  useEffect(() => {
    const pendingIds = Array.from(initialSelection.current.values())
      .filter((c) => c.pending)
      .map((c) => c.channelId);
    if (pendingIds.length === 0) return;
    let cancelled = false;
    getChannelGroupsByIds(pendingIds)
      .then((gs) => { if (!cancelled) setResolved(gs.flatMap((g) => g.channels)); })
      .catch(() => { /* names stay as stored — the entries are still removable */ });
    return () => { cancelled = true; };
  }, []);

  // Single upgrade path: whatever metadata we now hold replaces pending entries.
  // Keyed off `pending`, so this converges after one pass and never re-fires.
  useEffect(() => {
    const known = new Map<string, GroupedChannelOption>();
    for (const g of groups) for (const c of g.channels) known.set(c.channelId, c);
    for (const c of resolved) known.set(c.channelId, c);
    if (known.size === 0) return;
    onChange((prev) => {
      const next = new Map(prev);
      let changed = false;
      prev.forEach((entry, id) => {
        const match = entry.pending && known.get(id);
        if (!match) return;
        next.set(id, match);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [groups, resolved, onChange]);

  const toggleChannel = (ch: GroupedChannelOption) => {
    if (!selected.has(ch.channelId) && selected.size >= maxChannels) {
      notify('error', capMessage);
      return;
    }
    onChange((prev) => {
      const next = new Map(prev);
      if (next.has(ch.channelId)) next.delete(ch.channelId);
      else if (next.size < maxChannels) next.set(ch.channelId, ch);
      return next;
    });
  };

  /** Group checkbox: all selected → clear the group; otherwise select every channel (up to the cap). */
  const toggleGroup = (group: ChannelGroup) => {
    const additions = group.channels.filter((c) => !selected.has(c.channelId));
    if (additions.length > 0 && selected.size + additions.length > maxChannels) {
      notify('error', `${capMessage} — some were left unchecked`);
    }
    onChange((prev) => {
      const next = new Map(prev);
      if (group.channels.every((c) => next.has(c.channelId))) {
        group.channels.forEach((c) => next.delete(c.channelId));
        return next;
      }
      for (const c of group.channels) {
        if (next.has(c.channelId)) continue;
        if (next.size >= maxChannels) break;
        next.set(c.channelId, c);
      }
      return next;
    });
  };

  const query = channelQuery.trim();
  const newGroups = groups.filter((g) => g.kind === 'new');
  const payoutGroups = groups.filter((g) => g.kind !== 'new');
  const canLookUp = query.length >= 2 && lookedUpQuery !== query;

  const renderGroup = (g: ChannelGroup) => {
    const selCount = g.channels.filter((c) => selected.has(c.channelId)).length;
    const all = selCount === g.channels.length && g.channels.length > 0;
    const expanded = manualExpand.get(g.userId) ?? (query.length > 0 || g.kind === 'new');
    return (
      <div key={g.userId} style={{ borderBottom: '1px solid var(--border-1)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'var(--bg-surface-1)',
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            aria-label={`Select every channel of ${g.userName}`}
            checked={all}
            ref={(el) => { if (el) el.indeterminate = selCount > 0 && !all; }}
            onChange={() => toggleGroup(g)}
            style={{ cursor: 'pointer' }}
          />
          <button
            onClick={() => setManualExpand((prev) => new Map(prev).set(g.userId, !expanded))}
            style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'left', padding: 0 }}
          >
            <span>
              <span style={{ fontWeight: 600 }}>{g.userName}</span>
              {g.note && <span style={{ color: 'var(--fg-4)', fontSize: 11.5, marginLeft: 8 }}>{g.note}</span>}
            </span>
            <span style={{ color: 'var(--fg-4)', fontSize: 12, whiteSpace: 'nowrap' }}>
              {g.channels.length} channel{g.channels.length === 1 ? '' : 's'} {expanded ? '▾' : '▸'}
            </span>
          </button>
        </div>
        {expanded && g.channels.map((c) => (
          <label
            key={c.channelId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px 6px 32px',
              fontSize: 12.5,
              cursor: 'pointer',
              background: 'var(--bg-surface-2)',
            }}
          >
            <input type="checkbox" checked={selected.has(c.channelId)} onChange={() => toggleChannel(c)} style={{ cursor: 'pointer' }} />
            <span style={{ flex: 1 }}>{c.channelName}</span>
            {isPrePayout(c) ? (
              <span style={{ color: 'var(--fg-4)', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <NewTag />
                no payouts yet{c.sourceLabel ? ` · ${c.sourceLabel}` : ''}
              </span>
            ) : (
              <span style={{ color: isOutsideReportWindow(c.lastMonth) ? 'var(--warning)' : 'var(--fg-4)', fontSize: 11.5 }}>
                {c.firstMonth} → {c.lastMonth}{c.hasDirectSold ? ' · DS' : ''}
                {isOutsideReportWindow(c.lastMonth) ? ' · no data in the last 12 mo — dashboard would be empty' : ''}
              </span>
            )}
          </label>
        ))}
      </div>
    );
  };

  return (
    <div>
      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {Array.from(selected.values()).map((c) => (
            <span
              key={c.channelId}
              title={c.channelId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--bg-surface-2)',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12.5,
                opacity: c.pending ? 0.7 : 1,
              }}
            >
              {c.channelName}
              {isPrePayout(c) && <NewTag />}
              <button
                aria-label={`Remove ${c.channelName}`}
                onClick={() => toggleChannel(c)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--fg-4)', padding: 0 }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        style={fieldStyle}
        value={channelQuery}
        onChange={(e) => setChannelQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          lookUp();
        }}
        placeholder="Search by creator, channel name, UC id or YouTube link…"
        aria-label="Search channels"
      />
      <div style={{ fontSize: 11.5, color: 'var(--fg-4)', marginTop: 4, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>
          {looksLikeYouTubeChannel(query) && canLookUp
            ? 'Press Enter to look up this YouTube channel.'
            : 'Channels are grouped by creator/agency — selecting a creator checks every channel they run. New clients with no payouts yet are listed too.'}
        </span>
        {searching && <span aria-live="polite" style={{ whiteSpace: 'nowrap' }}>Searching…</span>}
      </div>
      {warnings.length > 0 && (
        <div role="status" style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {warnings.map((w) => (
            <div key={w} style={{ fontSize: 12, color: 'var(--fg-2)', background: 'var(--warning-soft)', borderRadius: 'var(--radius-sm)', padding: '6px 10px' }}>
              {w}
            </div>
          ))}
        </div>
      )}
      {groups.length > 0 ? (
        <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', marginTop: 6, maxHeight: 280, overflowY: 'auto' }}>
          {newGroups.length > 0 && <div style={sectionLabelStyle}>New clients · no payouts yet</div>}
          {newGroups.map(renderGroup)}
          {newGroups.length > 0 && payoutGroups.length > 0 && <div style={sectionLabelStyle}>Payout history</div>}
          {payoutGroups.map(renderGroup)}
        </div>
      ) : (
        !searching && query.length > 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--fg-3)', marginTop: 6, padding: '8px 12px', border: '1px dashed var(--border-1)', borderRadius: 'var(--radius-md)' }}>
            No channels match{canLookUp && !looksLikeYouTubeChannel(query) ? ' — press Enter to search ChannelMeter by name or email.' : '.'}
          </div>
        )
      )}
    </div>
  );
};

/** Shown under the picker when the selection includes channels with no payout yet. */
export const PrePayoutCallout: React.FC<{ selected: PickerSelection }> = ({ selected }) => {
  const count = Array.from(selected.values()).filter(isPrePayout).length;
  if (count === 0) return null;
  return (
    <div
      role="note"
      style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-2)', background: 'var(--info-soft)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}
    >
      <strong>
        {count} channel{count === 1 ? ' has' : 's have'} no payouts yet.
      </strong>{' '}
      {count === 1 ? 'Its' : 'Their'} part of the dashboard opens in a welcome view and fills in automatically after the first
      payout is published. The passcode and Tentpole Calendar work right away.
    </div>
  );
};
